import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import Button from '@renderer/components/bahela/Button'
import { FaVideo } from 'react-icons/fa'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency, roundMoney } from '@renderer/utils/formatCurrency'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaRed from '/assets/bahela/bahela-red.webp'
import printInfoId from '/assets/bahela/print-info-id.webp'
import printInfoEn from '/assets/bahela/print-info-en.webp'
import backButtonRed from '/assets/bahela/back-button-red.svg'
import arrow from '/assets/bahela/arrow.svg'
import twoPhotos from '/assets/bahela/2-photos.webp'
import fourPhotos from '/assets/bahela/2-photos.webp'

const CheckoutPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, transaction, logActivity, userPreferences } = useSessionStateStore()
  const customFrameSur = userPreferences.customFrameSurchargePerPackage ?? 0
  // Custom-frame voucher: recording is included free and the toggle is locked on.
  const recordingLocked = userPreferences.customFrameRecordingLocked === true
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.checkout
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)

  // States
  const [selectedPrintIndex, setSelectedPrintIndex] = useState(0)
  const [remainingTime, setRemainingTime] = useState('01:00')
  const [useRecording, setUseRecording] = useState(false)
  // When multiple recording packages exist, user chooses one (strip_live vs strip_story)
  const [selectedRecordingType, setSelectedRecordingType] = useState<
    'strip_live' | 'strip_story' | 'session_recording' | null
  >(null)

  // Recording packages from config (same as view: recording | strip_live | strip_story | session_recording)
  const recordingPackageTypes = [
    'recording',
    'strip_live',
    'strip_story',
    'session_recording'
  ] as const
  const recordingPackages = (config?.settings.packages ?? []).filter((p) =>
    recordingPackageTypes.includes(p.type as (typeof recordingPackageTypes)[number])
  )
  const hasMultipleRecordingOptions = recordingPackages.length > 1
  const hasRecordingOption = recordingPackages.length > 0

  // Single recording package for price/display (backward compat: first match or fallback)
  const recordingPackage =
    recordingPackages[0] ||
    ({
      type: 'recording',
      price: 5000,
      optional: true
    } as { type: string; price: number; optional: boolean })

  // Resolve recordingType for session: 'recording' -> 'strip_live' for backward compat
  const resolveRecordingType = (): 'strip_live' | 'strip_story' | 'session_recording' | undefined => {
    if (!useRecording) return undefined
    if (hasMultipleRecordingOptions && selectedRecordingType) return selectedRecordingType
    if (hasMultipleRecordingOptions) return 'strip_live' // default when multiple
    if (recordingPackages.length === 1) {
      const t = recordingPackages[0].type
      return t === 'recording' ? 'strip_live' : (t as 'strip_live' | 'strip_story' | 'session_recording')
    }
    return recordingPackage.type === 'recording'
      ? 'strip_live'
      : (recordingPackage.type as 'strip_live' | 'strip_story' | 'session_recording')
  }

  // Initialize countdown hook
  const checkoutCountdown = useCountdown(config?.settings?.checkoutTimeout || 60)

  // Get or create print packages
  const printPackages = [
    {
      id: '2 Strips',
      quantity: 1,
      price:
        config?.settings.packages?.find((p) => p.type === 'print' && p.quantity === 1)?.price ||
        35000,
      illustrationImgUrl: twoPhotos
    },
    {
      id: '4 Strips',
      quantity: 2,
      price:
        config?.settings.packages?.find((p) => p.type === 'print' && p.quantity === 2)?.price ||
        60000,
      illustrationImgUrl: fourPhotos
    }
  ]

  // Custom-frame voucher includes recording free of charge: force it on.
  useEffect(() => {
    if (recordingLocked && !useRecording) setUseRecording(true)
  }, [recordingLocked, useRecording])

  // Per-package recording price (0 when covered by a custom-frame voucher).
  const recordingPrice = recordingLocked ? 0 : recordingPackage.price

  // Initialize countdown
  useEffect(() => {
    // Set up countdown callbacks
    checkoutCountdown.setOnTick(() => {
      setRemainingTime(checkoutCountdown.getFormattedRemainingSeconds())
    })

    checkoutCountdown.setOnComplete(() => {
      continueToPayment()
    })

    checkoutCountdown.start()

    return (): void => {
      checkoutCountdown.stop()
    }
  }, [])

  const calculateDiscount = (basePrice: number): number => {
    const discountValue = transaction.discount || 0
    if (discountValue < 0) {
      // Negative value means percentage discount. Round to the currency's
      // minor unit so ringgit cents survive (a bare floor drops RM cents).
      return roundMoney((basePrice * Math.abs(discountValue)) / 100, projectCurrency)
    }
    // Positive value means fixed amount discount
    return discountValue
  }

  const getBasePrice = (packagePrice: number): number => {
    return packagePrice + customFrameSur + (useRecording ? recordingPrice : 0)
  }

  const calculateTotal = (packagePrice: number): number => {
    const basePrice = getBasePrice(packagePrice)
    const discount = calculateDiscount(basePrice)
    return roundMoney(Math.max(0, basePrice - discount), projectCurrency)
  }

  const continueToPayment = async (): Promise<void> => {
    const selectedPrint = printPackages[selectedPrintIndex]
    const basePrice = getBasePrice(selectedPrint.price)
    const finalDiscount = calculateDiscount(basePrice)
    const recordingType = resolveRecordingType()
    const pkgToSave =
      useRecording && recordingType
        ? recordingPackages.find(
            (p) => (p.type === 'recording' ? 'strip_live' : p.type) === recordingType
          ) || recordingPackage
        : null

    setSessionState({
      userPreferences: {
        copies: selectedPrint.quantity,
        useRecording,
        ...(recordingType != null && { recordingType })
      },
      transaction: {
        amount: calculateTotal(selectedPrint.price),
        discount: finalDiscount,
        selectedPackages: [
          {
            type: 'print',
            quantity: selectedPrint.quantity,
            price: selectedPrint.price
          },
          ...(useRecording && pkgToSave ? [pkgToSave] : [])
        ]
      }
    })

    // Log checkout selection activity
    logActivity('Checkout selection completed', {
      copies: selectedPrint.quantity,
      amount: calculateTotal(selectedPrint.price),
      discount: finalDiscount,
      useRecording
    })
    if (calculateTotal(selectedPrint.price) > 0) {
      navigate(`/${projectId}/payment`)
    } else {
      navigate(`/${projectId}/capture`)
    }
  }

  const moveLeft = (): void => {
    setSelectedPrintIndex((selectedPrintIndex - 1 + printPackages.length) % printPackages.length)
  }

  const moveRight = (): void => {
    setSelectedPrintIndex((selectedPrintIndex + 1) % printPackages.length)
  }

  // Call all hooks at the top level
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaRed = useCachedImage(bahelaRed)
  const cachedPrintInfo = useCachedImage(i18n.language === 'id' ? printInfoId : printInfoEn)
  const cachedBackButtonRed = useCachedImage(backButtonRed)
  const cachedArrow = useCachedImage(arrow)

  return (
    <div className="background relative bg-romantic w-full h-full overflow-hidden">
      <div
        className="deco-img absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />
      <img
        src={cachedBahelaRed}
        alt="Bahela Logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
      <img
        src={cachedPrintInfo}
        alt="Print Information"
        className="absolute z-[2] right-2 top-1/2 -translate-y-1/2 w-[543px] h-[393px]"
      />
      <div className="absolute z-[5] left-[50px] top-[102px] flex flex-col items-center">
        <button onClick={() => navigate(`/${projectId}/filter`)}>
          <img
            src={cachedBackButtonRed}
            className="w-[106px] h-[106px] btn-shadow"
            alt={t('common.back')}
          />
        </button>
        <span className="font-body-bahela text-[32px] text-methadone">{t('common.back')}</span>
      </div>
      <h1 className="font-serif-bahela z-[4] text-white drop-shadow-2xl absolute left-[111px] top-1/2 -translate-y-1/2 text-[100px] leading-none flex flex-col">
        {i18n.language === 'id' ? 'Mau ' : 'Printing '}
        <br />
        {i18n.language === 'id' ? 'Cetak ' : 'Options'}
        <br />
        {i18n.language === 'id' ? 'Berapa?' : ''}
      </h1>
      <div className="absolute z-[4] left-1/2 -translate-x-1/2 h-full w-auto gap-16 flex justify-center">
        <div className="h-full w-[93px] relative">
          <button className="absolute top-[187px] left-0 btn-shadow" onClick={moveLeft}>
            <img
              src={cachedArrow}
              className="h-[135px] w-[93px] object-contain drop-shadow-2xl"
              alt="Left Arrow"
            />
          </button>
        </div>
        <div className="h-full flex gap-2">
          {printPackages.map((print, index) => (
            <div
              key={print.id}
              className={
                'h-full p-[43px] flex flex-col justify-between items-center gap-[20px] ' +
                (index === 0 ? 'bg-hibiscus' : 'bg-prominentBlue')
              }
            >
              <div
                style={{
                  boxShadow: index === selectedPrintIndex ? '0 0 10px 5px rgba(0, 0, 0, 0.5)' : '',
                  transition: 'background-color 0.2s ease-out, box-shadow 0.2s ease-in-out'
                }}
                onClick={() => setSelectedPrintIndex(index)}
                className={
                  'flex flex-col cursor-pointer items-center rounded-3xl gap-[33px] p-[43px] mb-[20px] ' +
                  (index === selectedPrintIndex
                    ? 'bg-uriYellow box-shadow-active'
                    : ' drop-shadow-md hover:shadow-lg')
                }
              >
                <img
                  className="w-auto h-[523px] object-contain"
                  src={useCachedImage(print.illustrationImgUrl)}
                  alt={print.id}
                />
                <h2
                  className={
                    'font-serif-bahela text-[42px] ' +
                    (index === selectedPrintIndex
                      ? index === 0
                        ? 'text-hibiscus'
                        : 'text-prominentBlue'
                      : 'text-uriYellow')
                  }
                >
                  {print.id}
                </h2>
              </div>
              <div className={'text-center'}>
                <div className="w-[320px]">
                  <h2
                    className={`font-serif-bahela text-white whitespace-nowrap ${transaction.discount ? 'line-through decoration-methadone text-[35px]' : 'text-[70px]'}`}
                  >
                    {formatAmount(getBasePrice(print.price), projectCurrency)}
                  </h2>
                  {transaction.discount > 0 && (
                    <h2 className="font-serif-bahela text-white text-[70px] whitespace-nowrap">
                      {formatAmount(calculateTotal(print.price), projectCurrency)}
                    </h2>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="h-full w-[93px] relative">
          <button
            type={'button'}
            className="absolute top-[187px] left-0 btn-shadow"
            onClick={moveRight}
          >
            <img
              src={cachedArrow}
              className="h-[135px] w-[93px] object-contain rotate-180 drop-shadow-2xl"
              alt="Right Arrow"
            />
          </button>
        </div>
        <div className="absolute bottom-[220px] left-1/2 -translate-x-1/2 z-[5] flex flex-col items-center gap-4">
          <div
            onClick={() => {
              if (recordingLocked) return
              setUseRecording(!useRecording)
            }}
            className={`
              flex items-center gap-4 px-8 py-4
              rounded-full transition-all duration-300
              bg-uriYellow shadow-lg
              ${recordingLocked ? 'cursor-default opacity-90' : 'cursor-pointer hover:shadow-xl'}
            `}
          >
            <div className="flex items-center gap-3">
              <FaVideo size="24" color="#B3336C" />
              <div className="flex items-center gap-3 mr-12">
                <span className="font-serif-bahela text-[28px] text-hibiscus">
                  {i18n.language === 'id' ? 'Rekam dan Foto' : 'Record and Shoot'}
                </span>
                <span className="font-serif-bahela text-[24px] text-hibiscus opacity-75">
                  {recordingLocked
                    ? i18n.language === 'id'
                      ? '(Termasuk)'
                      : '(Included)'
                    : `(+${formatAmount(recordingPackage.price, projectCurrency)})`}
                </span>
              </div>
              <div
                className={`
                  w-14 h-8 rounded-full p-1 transition-colors duration-300 
                  ${useRecording ? 'bg-hibiscus' : 'bg-black/50'}
                `}
              >
                <div
                  className={`
                    w-6 h-6 rounded-full transition-transform duration-300 
                    bg-uriYellow shadow-md
                    ${useRecording ? 'translate-x-6' : 'translate-x-0'}
                  `}
                />
              </div>
            </div>
          </div>
          {/* Video output choice when multiple recording types in config */}
          {hasRecordingOption && useRecording && hasMultipleRecordingOptions && (
            <div className="flex gap-3">
              {recordingPackages.some((p) => p.type === 'strip_live' || p.type === 'recording') && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRecordingType('strip_live')
                  }}
                  className={`px-6 py-2 rounded-full font-body-bahela text-lg transition-all ${
                    (selectedRecordingType ?? 'strip_live') === 'strip_live'
                      ? 'bg-uriYellow text-methadone'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {t('recording.description.strip_live', 'Live')}
                </button>
              )}
              {recordingPackages.some((p) => p.type === 'strip_story') && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRecordingType('strip_story')
                  }}
                  className={`px-6 py-2 rounded-full font-body-bahela text-lg transition-all ${
                    selectedRecordingType === 'strip_story'
                      ? 'bg-uriYellow text-methadone'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {t('recording.description.strip_story', 'Story')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="absolute z-[6] right-[127px] bottom-[120px] drop-shadow-2xl flex flex-col items-center">
        <span className="text-[50px] text-white font-serif-bahela">{remainingTime}</span>
        <Button
          onClick={continueToPayment}
          className="text-[50px] bottom-[190px]"
          textColor="methadone"
        >
          {i18n.language === 'id' ? 'Bayar' : 'Pay'}
        </Button>
      </div>
    </div>
  )
}

export default CheckoutPage
